import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { confirmAlert } from 'react-confirm-alert';

import _cloneDeep from 'lodash/cloneDeep';

import { FormGroup, InputGroup, Row, Col } from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

import { entityMetaMapping, MetaForm, intlModule, pimModule } from '@chedri/base';

import { getAddressTypeOptions, COUNTRY_DEFAULT } from 'helpers/clients';

import Icon from 'components/Icon';
import IconLink from 'components/IconLink';

import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormCheckbox from 'components/forms/FormCheckbox';
import FormInput from 'components/forms/FormInput';
import Address from 'components/layout/contacts/Address';

const addressMetaId = entityMetaMapping.address;

function getCountryName(countries, key) {
  const country = countries.find(country => country.value === key);
  return country && country.label;
}

function getCountryLabel(countries, key) {
  const country = countries.find(country => country.value === key);
  return country?.label;
}

function AddressInput({ index, value, submitted, onError, onChange }) {
  const dispatch = useDispatch();

  const messages = useSelector(intlModule.selectors.getMessages);
  const lang = useSelector(intlModule.selectors.getEditLanguage);

  const countriesPending = useSelector(intlModule.countries.selectors.isListLoading);
  const countries = useSelector(intlModule.countries.selectors.getList);

  const addresses = useSelector(pimModule.addresses.selectors.getList);
  const addressesPending = useSelector(pimModule.addresses.selectors.isListLoading);

  const [addressSearch, setAddressSearch] = useState('');

  const [typeOptions, setTypeOptions] = useState([]);

  const [isEditable, setEditable] = useState(
    index != null && value && (!value[index].id || value[index].__origin != null)
  );

  const searchAddress = useCallback(search => {
    dispatch(pimModule.addresses.clearData());
    dispatch(pimModule.addresses.fetchList({ filters: { search } }));
  }, []);

  const addAddress = useCallback(() => {
    let newValue;
    if (value) {
      newValue = [
        ...value,
        {
          country: COUNTRY_DEFAULT,
          countryName: getCountryName(countries, COUNTRY_DEFAULT),
          types: ['mail', 'delivery', 'invoice'],
        },
      ];
    } else {
      newValue = [
        {
          country: COUNTRY_DEFAULT,
          countryName: getCountryName(countries, COUNTRY_DEFAULT),
          types: ['mail', 'delivery', 'invoice'],
        },
      ];
    }
    onChange(newValue);
  }, [value, countries, onChange]);

  const changeAddresses = useCallback(
    options => {
      // Takes an array as argument, because the typeahead component delivers one.
      if (options.length > 0) {
        let newValue;
        if (value) {
          newValue = [...value, options[0]];
        } else {
          newValue = [options[0]];
        }
        onChange(newValue);
      }
    },
    [value, onChange]
  );

  const makeEditable = useCallback(() => {
    confirmAlert({
      title: messages.address_change,
      message: messages.address_ask_change,
      buttons: [
        {
          label: messages.yes,
          onClick: () => {
            const newValue = [...value];
            newValue[index] = { ...value[index], __origin: _cloneDeep(value[index]) };
            onChange(newValue);
            setEditable(true);
          },
        },
        {
          label: messages.no,
          onClick: () => undefined,
        },
      ],
      closeOnEscape: true,
      closeOnClickOutside: true,
    });
  }, [value, index, messages, onChange]);


  useEffect(() => {
    if (!countries.length && !countriesPending) {
      dispatch(intlModule.countries.fetchList());
    }
  }, [countries, countriesPending]);

  useEffect(() => {
    setEditable(index != null && value && (!value[index].id || value[index].__origin != null));
  }, [value, setEditable]);

  useEffect(() => {
    setTypeOptions(getAddressTypeOptions(messages));
  }, []);

  return index == null ? (
    <>
      {Array.isArray(value) &&
        value.map((_, index) => (
          <AddressInput
            key={`address.${index}`}
            index={index}
            value={value}
            submitted={submitted}
            onChange={onChange}
          />
        ))}
      <Row>
        <Col xs={12} md={4}>
          <IconLink name="fa-plus" label={messages.new_address} onClick={addAddress} />
        </Col>
        <Col xs={12} md={8}>
          <FormGroup>
            <InputGroup>
              <InputGroup.Addon>
                <Icon name="fa-search-plus" />
              </InputGroup.Addon>
              <AsyncTypeahead
                id="searchAddressesInput"
                align="left"
                labelKey={() => addressSearch}
                query={addressSearch}
                isLoading={addressesPending}
                emptyLabel={messages.no_results_found}
                paginationText={messages.load_more}
                searchText={messages.searching}
                placeholder={messages.find_address}
                clearButton
                renderMenuItemChildren={option => <Address key={option.id} data={option} className="search-result" />}
                inputProps={{ autoComplete: 'no' }}
                filterBy={() => true}
                onInputChange={setAddressSearch}
                onChange={changeAddresses}
                onSearch={searchAddress}
                options={addresses}
                useCache={false}
              />
            </InputGroup>
          </FormGroup>
        </Col>
      </Row>
    </>
  ) : (
    <MetaForm
      metaId={addressMetaId}
      language={lang}
      source={value[index]}
      onChange={data => {
        /* Before we save the value, we change the
        country name based on the country code that was set via autocomplete.
        */
        const countryName = getCountryLabel(countries, data?.country);
        const newValue = [...value];
        newValue[index] = { ...data, countryName, __origin: value[index].__origin }; // Keep entry editable
        onChange(newValue);
      }}
    >
      {addressMetaContext => {
        const { getProperty, setProperty, errors } = addressMetaContext;
        if (typeof onError === 'function') {
          onError(errors);
        }
        const typesProp = getProperty('types');
        const typeValues = Array.isArray(typesProp) ? typesProp : typesProp.split(',');
        // TODO: move style to SASS file
        const lastAddressRowStyle = {'margin-bottom': '20px', 'border-bottom': '4px solid rgb(0, 0, 0, 0.07)'};

        return (
          <>
            {!isEditable ? (
              <div className="address-editable">
                <IconLink name="fa-edit" label={messages.edit} onClick={makeEditable} />
              </div>
            ) : null}
            <Row>
              <Col xs={12} className="inline">
                <FormCheckbox
                  name={`types.${index}`}
                  label={messages.type}
                  values={typeValues}
                  options={typeOptions}
                  onChange={setProperty('types')}
                  readonly={!isEditable}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <FormInput
                  name={`street.${index}`}
                  type="text"
                  label={messages.street}
                  value={getProperty('street')}
                  validation={submitted && errors ? errors.street : undefined}
                  onChange={setProperty('street')}
                  readonly={!isEditable}
                />
              </Col>
              <Col xs={6} sm={3}>
                <FormInput
                  name={`houseNumber.${index}`}
                  type="text"
                  label={messages.house_number}
                  value={getProperty('houseNumber')}
                  validation={submitted && errors ? errors.houseNumber : undefined}
                  onChange={setProperty('houseNumber')}
                  readonly={!isEditable}
                />
              </Col>
              <Col xs={6} sm={3}>
                <FormInput
                  name={`apartmentNumber.${index}`}
                  type="text"
                  label={messages.apartment_number}
                  value={getProperty('apartmentNumber')}
                  validation={submitted && errors ? errors.apartmentNumber : undefined}
                  onChange={setProperty('apartmentNumber')}
                  readonly={!isEditable}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormInput
                  name={`addressLine2.${index}`}
                  type="text"
                  label={messages.address_line2}
                  value={getProperty('addressLine2')}
                  validation={submitted && errors ? errors.addressLine2 : undefined}
                  onChange={setProperty('addressLine2')}
                  readonly={!isEditable}
                />
              </Col>
            </Row>
            <Row className="increased-input" style={lastAddressRowStyle}>
              <Col xs={6} sm={4}>
                <FormInput
                  name={`zipcode.${index}`}
                  type="text"
                  label={messages.zip_code}
                  value={getProperty('zipcode')}
                  validation={submitted && errors ? errors.zipcode : undefined}
                  onChange={setProperty('zipcode')}
                  readonly={!isEditable}
                />
              </Col>
              <Col xs={6} sm={4}>
                <FormInput
                  name={`city.${index}`}
                  type="text"
                  label={messages.city}
                  value={getProperty('city')}
                  validation={submitted && errors ? errors.city : undefined}
                  onChange={setProperty('city')}
                  readonly={!isEditable}
                />
              </Col>
              <Col xs={12} sm={4}>
                <FormAutocomplete
                  name={`country.${index}`}
                  options={countries}
                  label={messages.country}
                  value={getProperty('country', COUNTRY_DEFAULT)}
                  validation={submitted && errors ? errors.country : undefined}
                  onChange={(name, value) => {
                    setProperty('country')(value);
                  }}
                  readonly={!isEditable}
                />
              </Col>
            </Row>
          </>
        );
      }}
    </MetaForm>
  );
}

export default AddressInput;
